import AccessoryDrawer from 'components/accessories-page/accessory-drawer';
import AccessoryProductSearch from 'components/accessories-page/accessory-product-search';
import QuantityOption from 'components/accessories-page/quantity-option';
import XButton from 'components/inputs/x-button';
import { AccessoryProductType } from 'data/types';
import { useMemo } from 'react';
import {
  manualAccessoryKey,
  sortManualAccessoriesByName
} from 'utils/manual-accessories';
import type {
  ManualAccessoryChangeHandlerType,
  ManualAccessoryEntryType
} from 'utils/manual-accessories-types';

type AdditionalAccessoriesDrawerProps = {
  accessories: ManualAccessoryEntryType[];
  selectedProductIds: Set<number>;
  onChange: ManualAccessoryChangeHandlerType;
};

export default function AdditionalAccessoriesDrawer({
  accessories,
  selectedProductIds,
  onChange
}: AdditionalAccessoriesDrawerProps) {
  const sortedAccessories = useMemo(
    () => sortManualAccessoriesByName(accessories),
    [accessories]
  );

  const handleAdd = (product: AccessoryProductType) => {
    if (selectedProductIds.has(product.productId)) return;
    onChange(product, 1);
  };

  return (
    <div id='additional-accessories-drawer'>
      <AccessoryDrawer
        title='Additional Accessories'
        subtitle={
          accessories.length > 0
            ? `(${accessories.length} accessor${
                accessories.length > 1 ? 'ies' : 'y'
              } added)`
            : 'Add anything from the Alpine catalog the System Selector did not suggest.'
        }
        description='Search the Alpine catalog and add accessories to this quote manually, just like the PartnerNet quote system.'
        required={false}
        itemSelected={accessories.length > 0}
        notNeeded={false}
        handleNotNeeded={() => undefined}
        hideSwitch
        defaultOpen={accessories.length > 0}
      >
        <div className='flex flex-col gap-4 p-5'>
          {sortedAccessories.length > 0 && (
            <div className='flex flex-col divide-y divide-gray-100 rounded-lg border border-gray-200'>
              {sortedAccessories.map((accessory) => (
                <div
                  key={manualAccessoryKey(accessory)}
                  className='flex items-center pr-5'
                >
                  <div className='grow'>
                    <QuantityOption
                      accessory={accessory}
                      quantity={accessory.quantity}
                      size='compact'
                      onChange={(quantity) =>
                        onChange(accessory, quantity, accessory.ownerUniqueId)
                      }
                    />
                  </div>
                  <XButton
                    size='md'
                    color='gray'
                    tooltip='Remove accessory'
                    ariaLabel={`Remove ${accessory.name}`}
                    onClick={() =>
                      onChange(accessory, 0, accessory.ownerUniqueId)
                    }
                  />
                </div>
              ))}
            </div>
          )}
          <AccessoryProductSearch
            addedProductIds={selectedProductIds}
            onAdd={handleAdd}
          />
        </div>
      </AccessoryDrawer>
    </div>
  );
}
